import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Search, Star } from "lucide-react";
import { useFavorites, FavoriteItem } from "@/hooks/use-favorites";
import { useHaptics } from "@/hooks/use-haptics";

interface FavoritesConfig {
  type: FavoriteItem["type"];
  category: string;
  /** Given a row, return a unique id */
  makeId: (row: (string | number)[]) => string;
  /** Given a row, return a display name */
  makeName: (row: (string | number)[]) => string;
  /** Given a row, return a detail string */
  makeDetail: (row: (string | number)[]) => string;
}

interface DataTableProps {
  headers: string[];
  rows: (string | number)[][];
  searchable?: boolean;
  searchPlaceholder?: string;
  favoritesConfig?: FavoritesConfig;
}

const DataTable = ({ headers, rows, searchable = false, searchPlaceholder = "Filter…", favoritesConfig }: DataTableProps) => {
  const [query, setQuery] = useState("");
  const { toggleFavorite, isFavorite } = useFavorites();
  const { lightTap } = useHaptics();

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((row) => row.some((cell) => String(cell).toLowerCase().includes(q)));
  }, [rows, query]);

  return (
    <div className="space-y-2">
      {searchable && (
        <div className="relative mx-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="pl-9 bg-secondary border-border text-sm"
          />
        </div>
      )}
      <div className="overflow-x-auto mx-4 rounded-lg border border-border">
        <table className="w-full text-left">
          <thead>
            <tr>
              {favoritesConfig && <th className="data-table-header w-10"></th>}
              {headers.map((h) => (
                <th key={h} className="data-table-header">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={headers.length + (favoritesConfig ? 1 : 0)} className="data-table-cell text-center text-muted-foreground py-6">
                  No results found
                </td>
              </tr>
            ) : (
              filtered.map((row, i) => {
                const fav = favoritesConfig;
                const id = fav ? fav.makeId(row) : "";
                const faved = fav ? isFavorite(id) : false;
                return (
                  <tr key={i} className={i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                    {fav && (
                      <td className="data-table-cell w-10 px-2">
                        <button
                          onClick={() => {
                            lightTap();
                            toggleFavorite({
                              id,
                              type: fav.type,
                              name: fav.makeName(row),
                              detail: fav.makeDetail(row),
                              category: fav.category,
                            });
                          }}
                          className="p-1 rounded-md transition-colors hover:bg-secondary"
                          aria-label={faved ? "Remove from favorites" : "Add to favorites"}
                        >
                          <Star
                            className={`w-3.5 h-3.5 transition-colors ${
                              faved ? "fill-primary text-primary" : "text-muted-foreground"
                            }`}
                          />
                        </button>
                      </td>
                    )}
                    {row.map((cell, j) => (
                      <td key={j} className="data-table-cell">{cell}</td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataTable;
